<template>
  <div class="tongxin-date-selector">
    <h1 class="title-with-underline">請選擇日期</h1>

    <div class="s-mb-3">
      請選擇日期：<span v-if="date">已選擇 {{ selectedDateInString }}</span>
    </div>

    <div class="tongxin-date-selector__date s-mb-4">
      <Datepicker
        placeholder="請輸入查詢開始時間"
        format="yyyy/MM/dd"
        v-model="date"
        :language="zh"
        :disabled-dates="{ customPredictor: dateIsUnavailable }"
        calendar-class="datepicker-calendar"
        wrapper-class="datepicker-wrapper"
        bootstrap-styling
        inline
      />
    </div>

    <div class="tongxin-date-selector__notice" v-if="notes">
      <div class="tongxin-date-selector__notice-divider"></div>
      <div class="s-mb-3 s-font-medium s-pt-3">注意事項</div>
      <div class="s-text-sm s-text-gray" v-html="notes"></div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import { zh } from 'vuejs-datepicker/dist/locale';
import moment from 'moment';

export default {
  name: 'TongxinDateSelector',
  components: { Datepicker },
  props: {
    // Whitelist mode: only these dates are available (from API)
    availableDates: {
      type: Array,
      default: () => [],
    },
    // Individual business rule props for easier usage
    offDutyDates: {
      type: Array,
      default: () => [],
    },
    recurringOffDays: {
      type: Array,
      default: () => [],
    },
    bookingWindow: {
      type: Object,
      default: () => ({}),
    },
    leadTime: {
      type: Object,
      default: () => ({}),
    },
    workingHours: {
      type: Object,
      default: () => ({}),
    },
    // Legacy businessRules prop
    businessRules: {
      type: Object,
      default: () => ({}),
    },
    // Optional notes content (HTML ok)
    notes: {
      type: String,
      default: '',
    },
    value: {
      type: [Date, String],
      default: null
    }
  },
  data() {
    return {
      zh,
      date: null,
    };
  },
  watch: {
    // Watch for external value changes (from parent)
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue !== this.date) {
          this.date = newValue;
        }
      }
    },
    // Watch for internal date changes and emit to parent
    date: {
      handler(newDate) {
        // Clean up the date to remove time and emit only YYYY-MM-DD format
        if (newDate) {
          const formattedDate = moment(newDate).format('YYYY-MM-DD');
          this.$emit('input', formattedDate);
        } else {
          this.$emit('input', null);
        }
      }
    }
  },
  computed: {
    selectedDateInString() {
      return this.date ? moment(this.date).format('YYYY-MM-DD') : '';
    },
    // Computed property that merges individual props with businessRules
    mergedBusinessRules() {
      return {
        // Individual props take precedence over businessRules
        offDutyDates: this.offDutyDates.length > 0 ? this.offDutyDates : (this.businessRules.offDutyDates || []),
        recurringOffDays: this.recurringOffDays.length > 0 ? this.recurringOffDays : (this.businessRules.recurringOffDays || []),
        bookingWindow: Object.keys(this.bookingWindow).length > 0 ? this.bookingWindow : (this.businessRules.bookingWindow || {}),
        leadTime: Object.keys(this.leadTime).length > 0 ? this.leadTime : (this.businessRules.leadTime || {}),
        workingHours: Object.keys(this.workingHours).length > 0 ? this.workingHours : (this.businessRules.workingHours || {}),
      };
    },
  },
  methods: {
    // Check if date has passed
    dateHasPassed(date) {
      return moment(date).startOf('day').isBefore(moment().startOf('day'));
    },

    // Check if date is off-duty
    isOffDutyAtDate(date) {
      // Check recurring off-days first
      const dayOfWeek = moment(date).day();
      if (this.mergedBusinessRules.recurringOffDays && this.mergedBusinessRules.recurringOffDays.includes(dayOfWeek)) {
        return true;
      }
      
      // Check custom off-duty dates
      if (!this.mergedBusinessRules.offDutyDates || this.mergedBusinessRules.offDutyDates.length === 0) {
        return false;
      }
      
      const dateString = moment(date).format('YYYY-MM-DD');
      return this.mergedBusinessRules.offDutyDates.includes(dateString);
    },

    // Check booking window restrictions
    hasInsufficientBookingWindow(datetime) {
      const { type, amount } = this.mergedBusinessRules.bookingWindow;
      
      if (['none', null].includes(type) || !amount) {
        return false; // No restrictions
      }

      const now = moment();
      const targetDate = moment(datetime);
      
      if (type === 'day') {
        const maxFutureDate = now.clone().add(amount, 'days'); 
        return targetDate.isAfter(maxFutureDate);
      } else if (type === 'minute') {
        const maxFutureDate = now.clone().add(amount, 'minutes'); 
        return targetDate.isAfter(maxFutureDate);
      }
      
      return false;
    },

    // Check lead time restrictions
    hasInsufficientLeadTime(datetime) {
      const { type, amount } = this.mergedBusinessRules.leadTime;
      
      if (['none', null].includes(type) || !amount) {
        return false; // No restrictions
      }

      const now = moment();
      const targetDate = moment(datetime);
      
      if (type === 'day') {
        const minFutureDate = now.clone().add(amount, 'days'); 
        return targetDate.isBefore(minFutureDate);
      } else if (type === 'minute') {
        const minFutureDate = now.clone().add(amount, 'minutes'); 
        return targetDate.isBefore(minFutureDate);
      }
      
      return false;
    },
    dateIsUnavailable(date) {
      if (!date || !moment(date).isValid()) return false;

      const mDate = moment(date);
      const dateString = mDate.format('YYYY-MM-DD');

      if (mDate.isSame(moment(), 'day')) {
        return true;
      }

      // Whitelist mode only: empty array means no dates available
      if (this.availableDates.length === 0) {
        return true; // All dates unavailable
      }

      // Only dates in availableDates are available
      return !this.availableDates.includes(dateString);
    }

  },
};
</script>

<style lang="css" scoped>
.tongxin-date-selector {
  padding-bottom: 20px;
}

.tongxin-date-selector__date {
  position: relative;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar {
  border: 1px solid #E5E5EA;
  border-radius: 10px;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.15);
  padding: 10px;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell {
  position: relative;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.today:not(.selected) {
  background: transparent;
  color: var(--s-primary);
  border: none;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.today:not(.selected).disabled {
  color: #ddd;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.today:not(.selected)::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 37px;
  height: 37px;
  opacity: 0.3;
  border-radius: 50%;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.today:not(.selected)::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 37px;
  height: 37px;
  border: 2px dotted var(--s-primary);
  border-radius: 50%;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.selected,
.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.selected:hover {
  background: transparent;
  color: inherit; 
  border: none;
  position: relative;
  z-index: 0;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover::before,
.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.selected::before,
.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .cell.selected:hover::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 37px;
  height: 37px;
  border-radius: 50%;
  background: rgba(165, 214, 167, 0.3); /* light green background with transparency */
  z-index: -1;
}

.tongxin-date-selector ::v-deep .vdp-datepicker__calendar .day__month_btn {
  pointer-events: none;
}

.tongxin-date-selector__notice {
  margin-top: 16px;
}

.tongxin-date-selector__notice-divider {
  background: #E5E5EA;
  width: 100%;
  height: 1px;
  left: 0;
  position: absolute;
}
</style>
